import { NgTemplateOutlet } from "@angular/common";
import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";

import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";

import { TimelineBar } from "../timeline-bar/timeline-bar.component";
import { addDays, DayKey, diffDays, formatDay, isWeekend, minDay, maxDay, mondayOf, parseDay } from "../timeline-dates";
import { entryAccessibleName, PackedItem, PackOptions, packRow, pillWidth, pointText } from "../timeline-layout";
import { TimelineEntry, TimelineRow } from "../timeline.model";

interface MiniRowView {
  row: TimelineRow;
  items: PackedItem[];
  lanes: number;
  /** Grid line the label and backgrounds run to. */
  rowEnd: number;
}

interface MiniLayout {
  gridColumns: string;
  /** Wider than the box when pills need the room; the strip then pages sideways. */
  innerWidth: string;
  maxHeight: number;
  weeks: { key: string; text: string; column: number; span: number; month: boolean }[];
  weekends: { key: string; column: number; span: number }[];
  /** Runs of quiet weeks squeezed into one narrow column. */
  gaps: { key: string; column: number; span: number; text: string; label: string }[];
  todayColumn: number | null;
  /** Scroll offset that puts today at the left edge of the dates. */
  todayScrollPx: number;
  header: MiniRowView | null;
  rows: MiniRowView[];
  /** The header's date lines, carried down through the rows. */
  guides: { id: string; column: number; color: string }[];
}

const LANE_PX = 22;
const AXIS_PX = 24;
/** A collapsed stretch's label and width (in weekdays) by its length: 1 wk, 2–3 wks, 1 mo, then 2+ mo. */
function gapSize(days: number): { text: string; units: number } {
  if (days < 14) return { text: "1 wk", units: 1.5 };
  if (days < 28) return { text: `${Math.round(days / 7)} wks`, units: 2 };
  if (days < 56) return { text: "1 mo", units: 2.5 };
  return { text: `${Math.round(days / 30)} mo`, units: 3 };
}
/** Days left open beside the dates either side of a collapsed stretch. */
const QUIET_MARGIN_DAYS = 2;
const MIN_GAP_DAYS = 7;
/** Narrowest axis cell that gets its label; collapsed stretches are kept at least this wide. */
const MIN_LABEL_PX = 40;
/** Mini pills are longer than the Gantt's: 12px padding each side, at least 64px wide. */
const PILL: Pick<PackOptions, "pillPadding" | "pillMinWidth"> = { pillPadding: 30, pillMinWidth: 64 };

/**
 * A summary timeline that fits its dates: from 3 days before the first to 3 days after the last, weekends narrow.
 * Quiet stretches of a week or more (no dates, and bars only passing through without starting or ending) collapse
 * into one narrow column. Group rows become a date header band (their dates carry down through the other rows as muted lines); the other
 * rows follow, labeled with their label and sub text. If a pill would run into the next date on its row (more than
 * 2 days on, or any later date in the header), the days between widen and the strip pages sideways with the previous /
 * next buttons in the axis corner (no sideways scrolling); it shows the header and `visibleRows` rows before scrolling
 * down, opening on today. Dumb view: the caller picks the rows and entries.
 */
@Component({
  selector: "lib-timeline-mini",
  templateUrl: "./timeline-mini.component.html",
  styleUrl: "./timeline-mini.component.scss",
  imports: [MatIcon, MatIconButton, NgTemplateOutlet, TimelineBar],
})
export class TimelineMini {
  readonly rows = input.required<TimelineRow[]>();
  readonly entries = input.required<TimelineEntry[]>();
  readonly today = input.required<DayKey>();
  /** Accessible name of the strip. */
  readonly label = input("Timeline");
  readonly emptyText = input("No dates yet.");
  /** Width of a weekend day compared with a weekday. */
  readonly weekendWidth = input(0.4);
  /** Rows shown under the header before the strip scrolls down. */
  readonly visibleRows = input(2);
  /** Width of the row label column, in px. */
  readonly labelWidth = input(150);

  protected readonly entryAccessibleName = entryAccessibleName;

  readonly #width = signal(0);
  private readonly scroller = viewChild<ElementRef<HTMLElement>>("scroller");
  readonly #scrollLeft = signal(0);
  readonly #maxScroll = signal(0);
  protected readonly canPage = computed(() => this.#maxScroll() > 1);
  protected readonly atStart = computed(() => this.#scrollLeft() <= 1);
  protected readonly atEnd = computed(() => this.#scrollLeft() >= this.#maxScroll() - 1);

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const observer = new ResizeObserver(([entry]) => this.#width.set(entry.contentRect.width));
      observer.observe(host);
      destroyRef.onDestroy(() => observer.disconnect());
    });
    afterRenderEffect(() => {
      const scroller = this.scroller()?.nativeElement;
      const offset = this.#todayScrollPx();
      if (scroller && offset !== null) scroller.scrollLeft = offset;
      this.syncScroll();
    });
    afterRenderEffect(() => {
      this.layout();
      this.syncScroll();
    });
  }

  /** Moves the dates most of a visible width, keeping a little of the last view for context. */
  protected page(direction: 1 | -1): void {
    const scroller = this.scroller()?.nativeElement;
    if (!scroller) return;
    const datesPx = scroller.clientWidth - this.labelWidth();
    scroller.scrollBy({ left: direction * Math.max(40, datesPx * 0.8), behavior: "smooth" });
  }

  protected syncScroll(): void {
    const scroller = this.scroller()?.nativeElement;
    if (!scroller) return;
    this.#scrollLeft.set(scroller.scrollLeft);
    this.#maxScroll.set(scroller.scrollWidth - scroller.clientWidth);
  }

  protected readonly layout = computed<MiniLayout | null>(() => {
    const entries = this.entries();
    if (!entries.length) return null;
    const groupIds = new Set(
      this.rows()
        .filter((row) => row.isGroup)
        .map((row) => row.id)
    );
    const itemRows = this.rows().filter((row) => !row.isGroup);

    // Fit: 3 days either side of the dates shown.
    const start = addDays(entries.map((entry) => entry.start).reduce(minDay), -3);
    const end = addDays(entries.map((entry) => entry.end).reduce(maxDay), 3);
    const days = diffDays(start, end) + 1;
    const index = (day: DayKey) => diffDays(start, day);
    const quietRuns = this.#quietRuns(entries, start, end);
    const runOf = (i: number) => quietRuns.find((run) => i >= index(run.from) && i <= index(run.to));
    const shares = Array.from({ length: days }, (_, i) => {
      const run = runOf(i);
      if (run) {
        const length = diffDays(run.from, run.to) + 1;
        return gapSize(length).units / length;
      }
      return isWeekend(addDays(start, i)) ? this.weekendWidth() : 1;
    });
    const units = shares.reduce((sum, share) => sum + share, 0);

    // Widen the days after a date until its pill fits before the next date on its row more than 2 days on (close dates
    // stack instead); the header widens for any later date, so the project dates stay on one line unless they share a
    // day. Only those days grow (never a collapsed stretch), so the rest of the strip keeps its fit.
    const labelPx = this.labelWidth();
    const baseTrack = Math.max(200, (this.#width() || 1200) - labelPx);
    const dayWidths = shares.map((share) => (share * baseTrack) / units);
    for (const run of quietRuns) {
      const runDays = diffDays(run.from, run.to) + 1;
      const runPx = dayWidths.slice(index(run.from), index(run.to) + 1).reduce((sum, width) => sum + width, 0);
      if (runPx < MIN_LABEL_PX) {
        for (let day = index(run.from); day <= index(run.to); day++) dayWidths[day] = MIN_LABEL_PX / runDays;
      }
    }
    const byRow = new Map<string, TimelineEntry[]>();
    for (const entry of entries) {
      const key = groupIds.has(entry.rowId) ? "header" : entry.rowId;
      byRow.set(key, [...(byRow.get(key) ?? []), entry]);
    }
    for (const [key, rowEntries] of byRow) {
      const minGap = key === "header" ? 0 : 2;
      const points = rowEntries.filter((entry) => !entry.bar).sort((a, b) => a.start.localeCompare(b.start));
      points.forEach((point, i) => {
        const next = points.slice(i + 1).find((other) => diffDays(point.start, other.start) > minGap);
        const from = index(point.start);
        const to = next ? index(next.start) : days;
        const pill = pillWidth(pointText(point, "weeks"), !!point.warn, { dateStyle: "line", ...PILL });
        let openPx = 0;
        let gapPx = 0;
        for (let day = from; day < to; day++) {
          if (runOf(day)) gapPx += dayWidths[day];
          else openPx += dayWidths[day];
        }
        // Pills start half a day in, beside their line.
        const scale = (pill + 10 - gapPx) / (openPx - dayWidths[from] / 2);
        if (!(scale > 1) || !Number.isFinite(scale)) return;
        for (let day = from; day < to; day++) if (!runOf(day)) dayWidths[day] *= scale;
      });
    }
    const trackPx = dayWidths.reduce((sum, width) => sum + width, 0);
    const pxBefore = (day: number) => dayWidths.slice(0, day).reduce((sum, width) => sum + width, 0);
    const options: PackOptions = { scale: "weeks", dateStyle: "line", dayWidths, ...PILL };

    const view = (row: TimelineRow, rowEntries: TimelineEntry[]): MiniRowView => {
      const { items, lanes } = packRow(rowEntries, start, days, options);
      return { row, items, lanes, rowEnd: lanes + 1 };
    };
    const headerRow = this.rows().find((row) => row.isGroup);
    const header = headerRow && byRow.has("header") ? view(headerRow, byRow.get("header") ?? []) : null;
    const rows = itemRows.map((row) => view(row, byRow.get(row.id) ?? []));

    const weeks: MiniLayout["weeks"] = [];
    // Week labels, split around collapsed stretches; a week resuming after one is labeled from where it resumes.
    for (let week = mondayOf(start); week <= end; week = addDays(week, 7)) {
      let first: DayKey | null = null;
      const last = minDay(addDays(week, 6), end);
      for (let day = maxDay(week, start); day <= addDays(last, 1); day = addDays(day, 1)) {
        const open = day <= last && !runOf(index(day));
        if (open) {
          first ??= day;
        } else if (first) {
          const piecePx = dayWidths.slice(index(first), index(day)).reduce((sum, width) => sum + width, 0);
          weeks.push({
            key: first,
            // A sliver of a week beside a collapsed stretch keeps its divider but not a clipped date.
            text: piecePx >= MIN_LABEL_PX ? formatDay(first) : "",
            column: index(first) + 2,
            span: diffDays(first, day),
            month: parseDay(first).getDate() <= 7,
          });
          first = null;
        }
      }
    }
    const weekends: MiniLayout["weekends"] = [];
    for (let i = 0; i < days; i++) {
      if (!isWeekend(addDays(start, i)) || runOf(i)) continue;
      const last = weekends.at(-1);
      if (last && last.column + last.span === i + 2) last.span++;
      else weekends.push({ key: addDays(start, i), column: i + 2, span: 1 });
    }

    const rowPx = (lanes: number, minPx: number) => Math.max(lanes * LANE_PX + 8, minPx) + 1;
    return {
      gridColumns: `${labelPx}px ${dayWidths.map((width) => `minmax(0, ${width}fr)`).join(" ")}`,
      innerWidth: trackPx > baseTrack + 1 ? `${trackPx + labelPx}px` : "100%",
      maxHeight:
        AXIS_PX +
        (header ? rowPx(header.lanes, 0) : 0) +
        rows.slice(0, this.visibleRows()).reduce((sum, row) => sum + rowPx(row.lanes, 36), 0),
      weeks,
      weekends,
      gaps: quietRuns.map((run) => {
        const span = diffDays(run.from, run.to) + 1;
        return {
          key: run.from,
          column: index(run.from) + 2,
          span,
          text: gapSize(span).text,
          label: `${span} quiet days, ${formatDay(run.from)} to ${formatDay(run.to)}`,
        };
      }),
      todayColumn: this.today() >= start && this.today() <= end ? index(this.today()) + 2 : null,
      todayScrollPx: Math.max(0, pxBefore(Math.min(Math.max(index(this.today()), 0), days)) - 8),
      header,
      rows,
      guides: (header?.items ?? [])
        .filter((item) => !item.bar)
        .map((item) => ({ id: item.entry.id, column: item.column, color: item.entry.color })),
    };
  });

  protected readonly gapsDescription = computed(() => {
    const gaps = this.layout()?.gaps ?? [];
    return gaps.length ? `Collapsed: ${gaps.map((gap) => gap.label).join("; ")}` : null;
  });

  // A number, so realtime refreshes that leave today's spot alone don't reset the scroll.
  readonly #todayScrollPx = computed(() => this.layout()?.todayScrollPx ?? null);

  /**
   * Stretches with nothing starting or ending (no dates, and bars only passing through), less a couple of days' room
   * beside the dates around them, that still run a week or more. The bars still draw through the collapsed column.
   */
  #quietRuns(entries: TimelineEntry[], start: DayKey, end: DayKey): { from: DayKey; to: DayKey }[] {
    const busy = (day: DayKey) =>
      day === this.today() ||
      entries.some(
        (entry) => entry.start === day || entry.end === day || (!entry.bar && entry.start <= day && entry.end >= day)
      );
    const runs: { from: DayKey; to: DayKey }[] = [];
    let from: DayKey | null = null;
    for (let day = start; day <= addDays(end, 1); day = addDays(day, 1)) {
      if (day <= end && !busy(day)) {
        from ??= day;
        continue;
      }
      if (from) {
        const trimmed = { from: addDays(from, QUIET_MARGIN_DAYS), to: addDays(day, -1 - QUIET_MARGIN_DAYS) };
        if (diffDays(trimmed.from, trimmed.to) + 1 >= MIN_GAP_DAYS) runs.push(trimmed);
        from = null;
      }
    }
    return runs;
  }
}
